import { FC, ReactNode } from 'react';
import { IonCard } from '@ionic/react';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import useRoutes from '../../hooks/useRoutes';
import MasterFilter from '../../components/Manager/Filters/MasterFilter';
import SlaveFilter from '../../components/Manager/Filters/SlaveFilter';
import { GlobalLocationStateType, ReferenceControl } from '../../types/types';
import DetailFilterContextProvider from '../../context/DetailFilterContext';
import DatasetDetailReference from '../Reference/DatasetDetailReference';
import { useSelectedFiltersContext } from '../../hooks/context/useSelectedFiltersContext';
import { useFilterBackButtonGuard } from './hooks/useFilterBackButtonGuard';
import { getControlWithResolvedFilter } from '../../utils/filters/getControlWithResolvedFilter';

const FilterPageWrapper = ({ children }: { children: ReactNode }) => (
  <div className="page-container">
    <DetailFilterContextProvider>
      <IonCard>{children}</IonCard>
    </DetailFilterContextProvider>
  </div>
);

const DatasetDetailFilter: FC = () => {
  const { state, pathname } = useLocation<GlobalLocationStateType>();
  const routes = useRoutes();
  const history = useHistory();
  const match = useRouteMatch();
  const { dataset, setPeersetFilterFirstProcessed } = useDatasetContext();
  const { fullscreenReference, setFullscreenReference } =
    useSelectedFiltersContext();

  const { datasetId, subsetId, itemId } = useParams<{
    datasetId: string;
    itemId?: string;
    subsetId?: string;
  }>();

  useFilterBackButtonGuard();

  const isMasterFilter =
    pathname === routes.datasetDetailMasterFilter(datasetId);
  const isMasterReference =
    pathname === routes.masterFilterReference(datasetId);

  const closeFilter = (options?: { resetToDatasetList?: boolean }) => {
    const filterFirstObject = dataset?.filterFirst
      ? { omitFilterFirstFilter: true }
      : undefined;
    setPeersetFilterFirstProcessed(true);

    const returnUrl = isMasterFilter
      ? routes.datasetList(datasetId)
      : routes.subsetDetail(datasetId, itemId ?? '');

    // First filterFirst evaluation always lands on the original external URL.
    if (state?.fromFilterFirstInit) {
      history.push(state?.fromPathname ?? returnUrl, filterFirstObject);
      return;
    }

    if (options?.resetToDatasetList && isMasterFilter) {
      history.push(returnUrl, filterFirstObject);
      return;
    }

    history.push(state?.fromPathname ?? returnUrl, filterFirstObject);
  };

  const openFullscreenReference = (
    name: string,
    referenceDatasetId: string,
    control: ReferenceControl,
    value: string,
    label: string,
    resolvedFilter?: Record<string, any> | null
  ) => {
    setFullscreenReference({
      name,
      datasetId: referenceDatasetId,
      control: getControlWithResolvedFilter(control, resolvedFilter),
      value,
      label,
    });
    const url = isMasterFilter
      ? routes.masterFilterReference(datasetId)
      : routes.slaveFilterReference(datasetId, itemId ?? '', subsetId ?? '');

    // Need to pass state, to keep reference after closing reference page.
    history.push(url, state);
  };

  const commonProps = {
    closeFilter,
    openFullscreenReference,
  };

  if (
    !isMasterFilter &&
    !isMasterReference &&
    subsetId === undefined &&
    itemId === undefined
  ) {
    throw new Error('Subset ID is required for slave filters');
  }

  const resolvedFilter = isMasterFilter ? (
    <MasterFilter {...commonProps} />
  ) : (
    <SlaveFilter {...commonProps} subsetId={subsetId ?? ''} />
  );

  const DetailFilter = () => resolvedFilter;
  const ReferencePage = () => (
    <DatasetDetailReference
      fullscreenReference={fullscreenReference}
      setFullscreenReference={setFullscreenReference}
    />
  );

  /**
   * Sub route handling opening reference from master and slave detail filters.
   */
  return (
    <FilterPageWrapper>
      <Switch>
        {/* TODO: Using render prop is a hotfix to stop some of the unmounting and mounting of components.
        During refactoring we will need to update react-router to the latest version, rewrite routing according to
        their new syntax and hopefully fix routes randomly unmounting and mounting again */}
        {/* render prop avoids inline-component remount loop that triggers
            PrimeReact Tooltip.hide() -> setState during unmount cascade. */}
        <Route path={`${match.path}/reference`} render={ReferencePage} />
        <Route path={match.path} exact render={DetailFilter} />
      </Switch>
    </FilterPageWrapper>
  );
};

export default DatasetDetailFilter;
